import { isSectionLinkMagazine } from 'Webapp/predicates';
import { currentUserUid } from 'Webapp/shared/app/redux/selectors/auth';
import { FLAP_SEARCH_DEFAULT_PARAMS } from 'Webapp/shared/app/redux/actions/search-actions';
import flapUpdateFeed from 'Utils/api/flap/endpoints/update-feed';
import { FlapItemType, FlapSearchType, ItemType } from 'Webapp/enums';
import SectionUtil from 'Utils/content/section-util';
import { duc } from 'Utils/url';

export interface MentionableSuggestion {
  name: string;
  fullName: string;
  username: string;
  avatar: string;
  link: string;
  id: Flipboard.UserId;
}

// client-only, safe to cache
let zeroStateSuggestions: Array<MentionableSuggestion> = [];

export const getMentionSearchMagazineId = (
  item?: Flipboard.Item,
  section?: Flipboard.Section,
) => {
  const sectionLinkMagazine = item?.sectionLinks?.find(isSectionLinkMagazine);
  const refferedByStatus = item?.referredByItems?.find(
    (i) => i.type === ItemType.STATUS,
  );

  const referredByStatusSectionLinkMagazine =
    refferedByStatus?.sectionLinks.find(isSectionLinkMagazine);

  if (
    !sectionLinkMagazine &&
    !section &&
    !referredByStatusSectionLinkMagazine
  ) {
    return;
  }
  const id = (
    sectionLinkMagazine ||
    referredByStatusSectionLinkMagazine ||
    section
  )?.ssid?.remoteidPlain;
  // omg
  // auth%2Fflipboard%2Fcurator%252Fmagazine%252FRaDci9dPSGKEgiLuBhMGEQ%253Am%253A3619432980a
  // or
  // flipboard%2Fcurator%252Fmagazine%252FRaDci9dPSGKEgiLuBhMGEQ%253Am%253A3619432980a
  // to required format:
  // RaDci9dPSGKEgiLuBhMGEQ:m:3619432980a
  if (typeof id !== 'string') {
    return;
  }
  return duc(duc(id))
    .replace(/^auth\//, '')
    .replace(/^flipboard\/curator\/magazine\//, '');
};

// actions
export const fetchZeroStateMentionables =  // uses recent follows for now
  (): Flipboard.Thunk<Promise<Array<MentionableSuggestion>>> =>
  async (_dispatch, getState, { flap }) => {
    const {
      auth: { uid },
      profile: { followingIds },
    } = getState();
    if (zeroStateSuggestions.length > 0) {
      return zeroStateSuggestions;
    }
    const recentFollowingUserIds = (followingIds as string[])
      .filter((x) => x.indexOf('flipboard/user') === 0)
      .slice(-10)
      .reverse(); // most recent
    const params = {
      sections: recentFollowingUserIds.join(',') as Flipboard.SectionId,
    };

    const { data } = await flapUpdateFeed(flap, uid, params);
    const filteredToUserMetadataSection = data?.stream?.filter(
      (x) => x.section && x.type === FlapItemType.METADATA,
    );
    const suggestions = filteredToUserMetadataSection.map(({ section }) => {
      if (!section) {
        return undefined;
      }
      const projected = SectionUtil.projection(section) as Flipboard.Section;
      return {
        name: projected.authorUsername?.trim(),
        fullName: projected.authorDisplayName?.trim(),
        username: projected.authorUsername?.trim(),
        avatar: projected.authorImage,
        link: `/@${projected.authorUsername?.trim()}`,
        id: projected.userid,
      };
    }) as Array<MentionableSuggestion>;
    if (suggestions.length > 0) {
      zeroStateSuggestions = suggestions;
    }
    return suggestions;
  };

let searchMentionablesController: AbortController;
export const searchMentionables =
  (
    q: string,
    magazineId: Flipboard.SectionId,
  ): Flipboard.Thunk<Promise<Array<MentionableSuggestion>>> =>
  async (_dispatch, getState, { flap }) => {
    const uid = currentUserUid(getState());
    const params = {
      ...FLAP_SEARCH_DEFAULT_PARAMS,
      q,
      see_more: FlapSearchType.PROFILE,
      limit: 20,
      ...(magazineId ? { magazineId } : undefined),
    };
    searchMentionablesController?.abort();
    searchMentionablesController = new AbortController();
    const { data } = await flap.get<Flipboard.FlapSearchResults>(
      `/social/sectionSearch/${uid}`,
      { params, signal: searchMentionablesController.signal },
    );
    const profiles = data?.stream?.find(
      (flapSearchResult) =>
        flapSearchResult.category === FlapSearchType.PROFILE,
    );
    if (!profiles) {
      return [];
    }
    const suggestions = profiles.searchResultItems
      .filter((mentionable) => {
        if (q.indexOf(' ')) {
          const chunks = q.toLowerCase().split(' ');
          return chunks.every(
            (chunk: string) =>
              [
                mentionable.title.toLowerCase(),
                mentionable.username.toLowerCase(),
              ]
                .join()
                .indexOf(chunk) > -1,
          );
        }
        return mentionable;
      })
      .slice(0, 10)
      .map(
        (mentionable) =>
          ({
            /**
             *  name is important for draftJS mentionable suggestions.
             * DraftJS uses name for cursor handling, deleting, etc... It should match whatever
             * is displayed as the "mentionComponent" in the editor.
             * eg: "Comment text @mentionName".
             *
             * NOTE: If we ever revert the feature flag PILL_REMOVAL, we need to switch this back to "title".
             **/
            name: mentionable.username.trim(),
            fullName: mentionable.title.trim(),
            username: mentionable.username.trim(),
            avatar: mentionable.imageURL,
            link: `/@${mentionable.username}`,
            id: mentionable.userid,
          } as MentionableSuggestion),
      );
    return suggestions;
  };
